import React, { useEffect, useRef, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import '../css/ppe.css';

function PPE() {
    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
    }, []);

    const underlineRef = useRef(null);
    const [activeTab, setActiveTab] = useState('cotton');

    useEffect(() => {
        const timer = setTimeout(() => {
            if (underlineRef.current) {
                underlineRef.current.classList.add('animate');
            }
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    const coverallTypes = {
        cotton: {
            title: "100% Cotton Coverall",
            icon: "fas fa-tshirt",
            color: "#0c61c0",
            features: [
                "Soft and breathable fabric for maximum comfort",
                "Ideal for everyday industrial use",
                "Ensures comfort throughout long working hours",
                "Durable construction for extended wear",
                "Multiple pocket configurations",
                "Easy maintenance and washing"
            ],
            applications: [
                "General industrial work",
                "Manufacturing facilities",
                "Warehouse operations",
                "Maintenance work",
                "Light industrial tasks"
            ]
        },
        fr: {
            title: "Fire-Resistant (FR) Coverall",
            icon: "fas fa-fire-extinguisher",
            color: "#dc3545",
            features: [
                "Premium flame-retardant fabric",
                "Maximum protection in high-risk environments",
                "Certified for oil & gas industry",
                "Petrochemical-grade safety standards",
                "Self-extinguishing properties",
                "Heat and arc flash protection"
            ],
            applications: [
                "Oil & Gas operations",
                "Petrochemical facilities",
                "Refineries",
                "Welding operations",
                "High-temperature environments"
            ]
        }
    };

    const customizationOptions = [
        {
            icon: "fas fa-palette",
            title: "Multiple Color Options",
            desc: "Choose from a wide range of colors to match your company branding or safety requirements"
        },
        {
            icon: "fas fa-pen-fancy",
            title: "Custom Embroidery",
            desc: "Add your company logo, employee names, or safety messages with professional embroidery"
        },
        {
            icon: "fas fa-certificate",
            title: "International Standards",
            desc: "Full compliance with international safety standards and certifications"
        },
        {
            icon: "fas fa-cogs",
            title: "Tailor-Made Solutions",
            desc: "Customization based on specific requirements using our manufacturing capabilities"
        }
    ];

    return (
        <>
            {/* Hero Section */}
            <div className="ppe-hero-section" style={{ '--hero-bg': "url('/assets/coveralls-hero.jpg')" }}>
                <div className="ppe-hero-overlay"></div>
                <div className="container ppe-hero-content">
                    <div className="row align-items-center">
                        <div className="col-lg-6" data-aos="fade-right">
                            <h1 className="ppe-hero-title">Premium Industrial Coveralls</h1>
                            <div className="ppe-hero-underline" ref={underlineRef}></div>
                            <p className="ppe-hero-subtitle">
                                Experience the perfect balance of comfort, durability, and safety with our high-quality coveralls,
                                designed to meet the toughest industrial standards.
                            </p>
                            <div className="ppe-hero-badges">
                                <span className="ppe-badge">
                                    <i className="fas fa-shield-alt"></i> Safety Certified
                                </span>
                                <span className="ppe-badge">
                                    <i className="fas fa-award"></i> Premium Quality
                                </span>
                                <span className="ppe-badge">
                                    <i className="fas fa-industry"></i> Industrial Grade
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Types Section */}
            <section className="ppe-types-section">
                <div className="container">
                    <div className="text-center mb-5" data-aos="fade-up">
                        <h2 className="ppe-section-title">Our Coverall Types</h2>
                        <p className="ppe-section-subtitle">We manufacture two main types of industrial coveralls</p>
                    </div>

                    {/* Tab Navigation */}
                    <div className="ppe-tab-navigation" data-aos="fade-up">
                        <button
                            className={`ppe-tab-btn ${activeTab === 'cotton' ? 'active' : ''}`}
                            onClick={() => setActiveTab('cotton')}
                        >
                            <i className="fas fa-tshirt"></i>
                            <span>100% Cotton Coverall</span>
                        </button>
                        <button
                            className={`ppe-tab-btn ${activeTab === 'fr' ? 'active' : ''}`}
                            onClick={() => setActiveTab('fr')}
                        >
                            <i className="fas fa-fire-extinguisher"></i>
                            <span>Fire-Resistant Coverall</span>
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="ppe-tab-content">
                        {Object.keys(coverallTypes).map((type) => (
                            <div
                                key={type}
                                className={`ppe-tab-pane ${activeTab === type ? 'active' : ''}`}
                            >
                                <div className="row align-items-center">
                                    <div className="col-lg-6" data-aos="fade-right">
                                        <div className="ppe-product-image-wrapper">
                                            <div className="ppe-product-badge" style={{ background: coverallTypes[type].color }}>
                                                <i className={coverallTypes[type].icon}></i>
                                            </div>
                                            <img
                                                src={type === 'cotton' ? '/assets/cotton-coverall.jpg' : '/assets/fr-coverall.jpg'}
                                                alt={coverallTypes[type].title}
                                                className="ppe-product-image"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-6" data-aos="fade-left">
                                        <h3 className="ppe-product-title" style={{ color: coverallTypes[type].color }}>
                                            {coverallTypes[type].title}
                                        </h3>

                                        <div className="ppe-features-section">
                                            <h4 className="ppe-features-title">
                                                <i className="fas fa-check-circle"></i> Key Features
                                            </h4>
                                            <ul className="ppe-features-list">
                                                {coverallTypes[type].features.map((feature, index) => (
                                                    <li key={index}>
                                                        <i className="fas fa-chevron-right"></i>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="ppe-applications-section">
                                            <h4 className="ppe-features-title">
                                                <i className="fas fa-industry"></i> Applications
                                            </h4>
                                            <div className="ppe-applications-grid">
                                                {coverallTypes[type].applications.map((app, index) => (
                                                    <span key={index} className="ppe-application-tag">
                                                        {app}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Customization Section */}
            <section className="ppe-customization-section">
                <div className="container">
                    <div className="text-center mb-5" data-aos="fade-up">
                        <h2 className="ppe-section-title text-white">Customization Options</h2>
                        <p className="ppe-section-subtitle text-white-50">
                            Combining functionality, protection, and a professional look for every job site
                        </p>
                    </div>

                    <div className="row g-4">
                        {customizationOptions.map((option, index) => (
                            <div key={index} className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay={index * 100}>
                                <div className="ppe-custom-card">
                                    <div className="ppe-custom-icon">
                                        <i className={option.icon}></i>
                                    </div>
                                    <h5 className="ppe-custom-title">{option.title}</h5>
                                    <p className="ppe-custom-desc">{option.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Full Customization Banner */}
            <section className="ppe-banner-section">
                <div className="container">
                    <div className="ppe-banner-card" data-aos="zoom-in">
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <h3 className="ppe-banner-title">
                                    <i className="fas fa-tools"></i>
                                    Full Customization Available
                                </h3>
                                <p className="ppe-banner-text">
                                    We provide full customization options based on our customers' specific requirements,
                                    utilizing our manufacturing capabilities and reliable resources to deliver tailor-made
                                    solutions that perfectly match every industrial need.
                                </p>
                            </div>
                            <div className="col-lg-4 text-end">
                                <a href="/quote" className="ppe-cta-button">
                                    Get a Quote
                                    <i className="fas fa-arrow-right ms-2"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="ppe-why-section">
                <div className="container">
                    <div className="text-center mb-5" data-aos="fade-up">
                        <h2 className="ppe-section-title">Why Choose Petrochoice Coveralls?</h2>
                    </div>

                    <div className="row g-4">
                        {[
                            {
                                icon: "fas fa-industry",
                                title: "Industrial Expertise",
                                desc: "Decades of experience in providing safety solutions for the oil & gas industry"
                            },
                            {
                                icon: "fas fa-certificate",
                                title: "Quality Assurance",
                                desc: "All products meet or exceed international safety standards and certifications"
                            },
                            {
                                icon: "fas fa-shipping-fast",
                                title: "Reliable Delivery",
                                desc: "Timely delivery and consistent supply chain management"
                            },
                            {
                                icon: "fas fa-headset",
                                title: "Customer Support",
                                desc: "Dedicated support team to assist with selection and customization"
                            },
                            {
                                icon: "fas fa-dollar-sign",
                                title: "Competitive Pricing",
                                desc: "Best value for premium quality industrial safety equipment"
                            },
                            {
                                icon: "fas fa-recycle",
                                title: "Sustainable Materials",
                                desc: "Environmentally conscious manufacturing processes and materials"
                            }
                        ].map((item, index) => (
                            <div key={index} className="col-lg-4 col-md-6" data-aos="fade-up" data-aos-delay={index * 100}>
                                <div className="ppe-why-card">
                                    <div className="ppe-why-icon">
                                        <i className={item.icon}></i>
                                    </div>
                                    <h5 className="ppe-why-title">{item.title}</h5>
                                    <p className="ppe-why-desc">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="ppe-cta-section">
                <div className="container">
                    <div className="ppe-cta-card" data-aos="zoom-in">
                        <h2 className="ppe-cta-title">Ready to Protect Your Team?</h2>
                        <p className="ppe-cta-text">
                            Contact us today to discuss your industrial coverall requirements and get a customized solution
                        </p>
                        <div className="ppe-cta-buttons">
                            <a href="/contact" className="ppe-btn ppe-btn-primary">
                                <i className="fas fa-phone-alt me-2"></i>
                                Contact Us
                            </a>
                            <a href="/quote" className="ppe-btn ppe-btn-secondary">
                                <i className="fas fa-file-invoice me-2"></i>
                                Request Quote
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default PPE;
